

import React, { createContext, useContext, useCallback, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { LuAudioLines, LuVideo, LuImage, LuFile } from 'react-icons/lu'
import { renderToStaticMarkup } from 'react-dom/server'

const MEDIA_ICONS = {
  image: LuImage,
  video: LuVideo,
  audio: LuAudioLines,
}

const DragPreviewContext = createContext(null)

export function useDragPreview() {
  const ctx = useContext(DragPreviewContext)
  if (!ctx) throw new Error('useDragPreview must be used within DragPreviewProvider')
  return ctx
}

export function DragPreviewProvider({ children }) {
  const overlayRef = useRef(null)
  const rafRef = useRef(null)
  const posRef = useRef({ x: 0, y: 0 })
  const activeRef = useRef(false)

  const getOverlay = useCallback(() => {
    if (overlayRef.current) return overlayRef.current
    const el = document.createElement('div')
    el.style.cssText = 'position:fixed;z-index:9999;pointer-events:none;display:none;transform:translateY(-50%);'
    document.body.appendChild(el)
    overlayRef.current = el
    return el
  }, [])

  useEffect(() => {
    return () => {
      if (overlayRef.current) {
        overlayRef.current.remove()
        overlayRef.current = null
      }
    }
  }, [])

  const show = useCallback((e, data) => {
    // Suppress native ghost with an offscreen element
    const ghost = getOverlay()
    const offscreen = document.createElement('div')
    offscreen.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;opacity:0;'
    document.body.appendChild(offscreen)
    e.dataTransfer.setDragImage(offscreen, 0, 0)
    requestAnimationFrame(() => offscreen.remove())

    // Build preview HTML
    const { thumbnailUrl, name, mediaType } = data
    const iconMap = { image: 'LuImage', video: 'LuVideo', audio: 'LuAudioLines' }
    const iconSvg = getIconSvg(mediaType)

    ghost.innerHTML = `
      <div style="width:80px;border-radius:6px;overflow:hidden;background:var(--card);border:1px solid var(--secondary);box-shadow:0 4px 12px rgba(0,0,0,0.3);">
        <div style="width:100%;aspect-ratio:16/9;position:relative;display:flex;align-items:center;justify-content:center;background:color-mix(in srgb, var(--secondary) 60%, transparent);">
          ${thumbnailUrl
            ? `<img src="${thumbnailUrl}" style="width:100%;height:100%;object-fit:cover;" draggable="false" />`
            : `<div style="color:color-mix(in srgb, var(--secondary-foreground) 50%, transparent);">${iconSvg}</div>`
          }
        </div>
        ${name ? `<div style="padding:2px 4px;"><span style="font-size:9px;color:var(--secondary-foreground);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:block;line-height:1.2;">${escapeHtml(name)}</span></div>` : ''}
      </div>
    `

    posRef.current = { x: e.clientX, y: e.clientY }
    ghost.style.left = (e.clientX + 12) + 'px'
    ghost.style.top = e.clientY + 'px'
    ghost.style.display = 'block'
    activeRef.current = true

    let heartbeat = null
    const resetHeartbeat = () => {
      if (heartbeat) clearTimeout(heartbeat)
      heartbeat = setTimeout(onEnd, 300)
    }

    const onDragOver = (ev) => {
      posRef.current = { x: ev.clientX, y: ev.clientY }
      resetHeartbeat()
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(() => {
          ghost.style.left = (posRef.current.x + 12) + 'px'
          ghost.style.top = posRef.current.y + 'px'
          rafRef.current = null
        })
      }
    }

    const onEnd = () => {
      ghost.style.display = 'none'
      ghost.innerHTML = ''
      activeRef.current = false
      if (heartbeat) { clearTimeout(heartbeat); heartbeat = null }
      document.removeEventListener('dragover', onDragOver)
      document.removeEventListener('dragend', onEnd)
      document.removeEventListener('drop', onEnd)
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }

    resetHeartbeat()
    document.addEventListener('dragover', onDragOver)
    document.addEventListener('dragend', onEnd)
    document.addEventListener('drop', onEnd)
  }, [getOverlay])

  const hide = useCallback(() => {
    const ghost = overlayRef.current
    if (ghost) {
      ghost.style.display = 'none'
      ghost.innerHTML = ''
    }
    activeRef.current = false
  }, [])

  const ctxValue = useRef({ showDragPreview: show, hideDragPreview: hide })
  ctxValue.current.showDragPreview = show
  ctxValue.current.hideDragPreview = hide

  return (
    <DragPreviewContext.Provider value={ctxValue.current}>
      {children}
    </DragPreviewContext.Provider>
  )
}

function escapeHtml(str) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function getIconSvg(mediaType) {
  const icons = {
    image: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>',
    video: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"/><rect x="2" y="6" width="14" height="12" rx="2"/></svg>',
    audio: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 10v3"/><path d="M6 6v11"/><path d="M10 3v18"/><path d="M14 8v7"/><path d="M18 5v13"/><path d="M22 10v3"/></svg>',
  }
  return icons[mediaType] || '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/></svg>'
}
